import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useRef } from 'react';
import { useParams } from '@tanstack/react-router';

import { useAuth } from '@/hooks/use-auth';
import { useUser } from '@/hooks/use-user';
import { notifyLaunchDarklyIdentified } from '@/state/launch-darkly.state';
import { buildLDMultiContext } from '@/utils/launch-darkly-helpers';
import { type LDContextLatch, resolveLDContextLatch } from '@/utils/launch-darkly-latch';

import { useCurrentFacility } from './use-current-facility';

/**
 * Use LaunchDarkly Context
 * Sets the LaunchDarkly context for the current user, organization, facility, and agent.
 *
 * Never downgrades a complete context for the same Auth0 `sub` (fills transient gaps from an
 * in-memory latch). Clears the latch on logout or identity change so one user's data never
 * rides to the next.
 */
export const useLaunchDarklyContext = () => {
  const { userId, email, name } = useUser();
  const { organizationId, facilityId, facilityUrlName } = useCurrentFacility();
  const { isAuthenticated, user } = useAuth();
  const ldClient = useLDClient();
  const params = useParams({ strict: false });
  const routeAgent = params['agent'] as string | undefined;

  const latchRef = useRef<LDContextLatch | null>(null);
  const lastIdentifiedRef = useRef<string | null>(null);
  const identifyGenRef = useRef(0);

  useEffect(() => {
    const sub = user?.sub;
    const priorSub = latchRef.current?.sub;

    const result = resolveLDContextLatch({
      prior: latchRef.current,
      isAuthenticated,
      sub,
      userId,
      email,
      name,
      organizationId,
      facilityId,
      facilityUrlName,
      routeAgent,
    });

    if (result.action === 'clear') {
      latchRef.current = null;
      lastIdentifiedRef.current = null;
      identifyGenRef.current += 1;
      return;
    }

    // Sub change: drop stale identify bookkeeping even though resolve already ignored prior.
    if (priorSub != null && sub != null && priorSub !== sub) {
      lastIdentifiedRef.current = null;
      identifyGenRef.current += 1;
    }

    latchRef.current = result.latch;

    if (result.action === 'defer' || result.action === 'wait') {
      return;
    }

    if (!ldClient) {
      return;
    }

    const ldContext = buildLDMultiContext({
      email: result.resolved.email,
      userId: result.resolved.userId,
      name: result.resolved.name,
      organizationId: result.resolved.organizationId,
      facilityId: result.resolved.facilityId,
      facilityUrlName: result.resolved.facilityUrlName,
    });

    if (!ldContext) {
      return;
    }

    const serialized = JSON.stringify(ldContext);
    if (serialized === lastIdentifiedRef.current) {
      return;
    }

    const gen = ++identifyGenRef.current;
    ldClient.identify(ldContext).then(
      () => {
        if (gen !== identifyGenRef.current) return;
        lastIdentifiedRef.current = serialized;
        notifyLaunchDarklyIdentified();
      },
      (error) => {
        if (gen !== identifyGenRef.current) return;
        console.error('[LaunchDarkly] Failed to identify context:', error);
      }
    );
  }, [
    userId,
    email,
    name,
    organizationId,
    facilityId,
    facilityUrlName,
    ldClient,
    isAuthenticated,
    user?.sub,
    routeAgent,
  ]);
};
